import React, { useEffect, useState } from "react";
import { Camera, CameraOff, Send, Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export const consentState = (m) => (m?.media_consent === true ? "granted" : m?.media_consent === false ? "declined" : "unanswered");
const LABEL = { granted: "Consented", declined: "Declined", unanswered: "Not answered" };
const PILL = { granted: "bg-palm/15 text-palm", declined: "bg-red-100 text-red-800", unanswered: "bg-amber-100 text-amber-800" };

export function ConsentBadge({ member, compact = false }) {
  const st = consentState(member);
  if (compact && st === "granted") return null;
  const Icon = st === "granted" ? Camera : CameraOff;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${PILL[st]}`}
      title={`Photo & video consent: ${LABEL[st]}${member?.media_consent_at ? ` (${member.media_consent_at.slice(0, 10)})` : ""}`} data-testid={`consent-badge-${member?.id}`}>
      <Icon size={10} /> {st === "granted" ? "Photos OK" : "No media"}
    </span>
  );
}

export function MyConsentCard({ user, onSaved }) {
  const { data: txt } = useApi("/consent/text");
  const [busy, setBusy] = useState(false);
  const st = consentState(user);
  const set = async (granted) => {
    setBusy(true);
    try { await api.put("/members/me/media-consent", { granted }); toast.success(granted ? "Thank you — photos and videos of you may be published" : "Noted — we will not publish photos or videos of you"); onSaved?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="glass rounded-2xl p-6" data-testid="my-consent-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-serif-display text-xl font-bold text-crimson">Photo &amp; video consent</h3>
          <p className="mt-1 text-sm text-muted-foreground">{txt?.text}</p>
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${PILL[st]}`} data-testid="my-consent-state">{LABEL[st]}</span>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gold/30 bg-parchment-card p-3">
        <Label htmlFor="consent-switch" className="text-sm">{st === "granted" ? "You have consented" : "I consent to my photos/videos being published"}</Label>
        <span className="flex items-center gap-3">
          {st === "unanswered" && <Button size="sm" variant="outline" disabled={busy} onClick={() => set(false)} data-testid="my-consent-decline">No thanks</Button>}
          <Switch id="consent-switch" checked={st === "granted"} disabled={busy} onCheckedChange={set} data-testid="my-consent-switch" />
        </span>
      </div>
      {st === "unanswered" && <p className="mt-2 text-xs text-amber-800" data-testid="my-consent-unanswered">You have not answered yet — until you do, we treat it as <b>no consent</b>.</p>}
      {user?.media_consent_at && <p className="mt-2 text-xs text-muted-foreground">Last updated {new Date(user.media_consent_at).toLocaleString()} · recorded in your profile documentation</p>}
    </div>
  );
}

export function OfficerConsentControl({ member: initial }) {
  const [member, setMember] = useState(initial);
  const [note, setNote] = useState("");
  const st = consentState(member);
  const set = async (granted) => {
    try { const { data } = await api.put(`/admin/members/${member.id}/media-consent`, { granted, note }); setMember(data); toast.success("Consent recorded"); setNote(""); window.dispatchEvent(new Event("consent-changed")); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-lg border border-gold/30 bg-gold-light/50 p-3 sm:col-span-2" data-testid="officer-consent-control">
      <div className="flex items-center justify-between gap-3">
        <div><Label>Photo &amp; video consent</Label><p className="text-xs text-muted-foreground">Currently <b>{LABEL[st]}</b>{member.media_consent_at ? ` since ${member.media_consent_at.slice(0, 10)} (${member.media_consent_source || "portal"})` : ""}. Record a paper/verbal consent on the member's behalf — say how it was obtained.</p></div>
        <ConsentBadge member={member} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Input placeholder="How obtained — e.g. signed paper form at AGM 2026" value={note} onChange={(e) => setNote(e.target.value)} className="h-8 flex-1 text-xs" data-testid="officer-consent-note" />
        <Button size="sm" className="bg-palm hover:bg-palm/90" disabled={!note.trim()} onClick={() => set(true)} data-testid="officer-consent-grant">Mark consented</Button>
        <Button size="sm" variant="outline" className="border-red-300 text-red-700" disabled={!note.trim()} onClick={() => set(false)} data-testid="officer-consent-decline">Mark declined</Button>
      </div>
      {member.media_consent_history?.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground" data-testid="consent-history">
          {[...member.media_consent_history].reverse().slice(0, 5).map((h, i) => <li key={i}>{h.at.slice(0, 10)} · {h.granted ? "consented" : "declined"} via {h.source} by {h.by}{h.note ? ` — ${h.note}` : ""}</li>)}
        </ul>)}
    </div>
  );
}

export function ConsentPanel({ isAdminLevel, onChanged }) {
  const { data, reload } = useApi("/admin/media-consent/summary");
  const [busy, setBusy] = useState(false);
  useEffect(() => { const h = () => { reload(); onChanged?.(); }; window.addEventListener("consent-changed", h); return () => window.removeEventListener("consent-changed", h); }, [reload, onChanged]);
  if (!data) return null;
  const c = data.counts;
  const nudge = async () => {
    if (!window.confirm(`Email the ${data.nudgeable} active member(s) who have never answered and never been asked, pointing them to the portal? Each member is only ever asked once.`)) return;
    setBusy(true);
    try { const { data: r } = await api.post("/admin/media-consent/nudge"); toast.success(`${r.sent} of ${r.targets} members emailed${r.skipped ? ` · ${r.skipped} skipped (email test mode) — they will be asked next time` : ""}`); reload(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const grandfather = async () => {
    const note = window.prompt(`Board opt-out policy: mark all ${c.unanswered} members who have never answered as CONSENTED (members who declined stay declined). Enter the authority for this, e.g. "Board resolution 12 Nov 2026":`);
    if (!note || note.trim().length < 5) return;
    if (window.prompt("Type GRANDFATHER to confirm") !== "GRANDFATHER") return;
    setBusy(true);
    try { const { data: r } = await api.post("/admin/media-consent/grandfather", { confirm: "GRANDFATHER", note }); toast.success(`${r.updated} members marked as consented`); reload(); onChanged?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const csv = () => {
    const rows = [["Name", "Member ID", "Chapter", "Status"], ...data.no_publish.map((m) => [m.name, m.member_id, m.chapter, LABEL[m.state]])];
    const blob = new Blob([rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""').replace(/^([=+\-@])/, "'$1")}"`).join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "do-not-publish-list.csv"; a.click(); URL.revokeObjectURL(a.href);
  };
  return (
    <div className="mb-6 glass rounded-2xl p-5" data-testid="consent-panel">
      <div className="flex flex-wrap items-center gap-3">
        <CameraOff size={18} className="text-crimson" />
        <h3 className="font-serif-display text-lg font-bold text-crimson">Photo &amp; video consent</h3>
        <span className="rounded-full bg-palm/15 px-2 py-0.5 text-xs font-semibold text-palm" data-testid="consent-count-granted">{c.granted} consented</span>
        <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800" data-testid="consent-count-declined">{c.declined} declined</span>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800" data-testid="consent-count-unanswered">{c.unanswered} not answered</span>
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={csv} data-testid="consent-dnp-csv"><Download size={14} className="mr-1" /> Do-not-publish list</Button>
          {isAdminLevel && data.nudgeable > 0 && <Button size="sm" className="bg-crimson hover:bg-crimson-hover" disabled={busy} onClick={nudge} data-testid="consent-nudge"><Send size={14} className="mr-1" /> Ask {data.nudgeable} unanswered</Button>}
          {isAdminLevel && c.unanswered > 0 && <Button size="sm" variant="outline" className="border-gold/60" disabled={busy} onClick={grandfather} title="Board opt-out policy: treat everyone who never answered as consented" data-testid="consent-grandfather"><ShieldCheck size={14} className="mr-1" /> Grandfather {c.unanswered}</Button>}
        </span>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Members who have not consented (declined or never answered) must not appear in published photos or videos. The roll-call for each meeting flags them, and the list above can be handed to the photographer.</p>
    </div>
  );
}
